import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Button } from '@eltoro-ui/components'
import classNames from 'classnames'

// Tooltips struggle in the table due to overflow hidden and z-index issues.
// This is a custom tooltip implementation that uses portals to render
// the tooltip at the body level to avoid these issues.
const CustomTooltip = ({
  content,
  targetRef,
  show,
}: {
  content: string
  targetRef: React.RefObject<HTMLButtonElement>
  show: boolean
}) => {
  const [position, setPosition] = useState({ top: 0, left: 0 })

  useEffect(() => {
    if (show && targetRef.current) {
      const rect = targetRef.current.getBoundingClientRect()
      setPosition({
        top: rect.top + rect.height / 2,
        left: rect.left - 16,
      })
    }
  }, [show, targetRef])

  if (!show) return null

  const tooltipStyle: React.CSSProperties = {
    position: 'fixed',
    top: `${position.top}px`,
    left: `${position.left}px`,
    transform: 'translateY(-50%) translateX(-100%)',
    zIndex: 99999,
    pointerEvents: 'none',
    marginRight: '1rem',
  }

  const contentStyle: React.CSSProperties = {
    backgroundColor: 'var(--base-bg)',
    color: 'var(--base-text)',
    border: '1px solid var(--base-bg)',
    borderRadius: '0.25rem',
    padding: '0.5rem 0.625rem 0.55rem',
    fontSize: '0.875rem',
    fontWeight: 600,
    textAlign: 'center',
    minWidth: '10rem',
    maxWidth: '400px',
    filter: 'drop-shadow(-1px 3px 5px rgba(0, 0, 0, 0.25))',
    lineHeight: '1.2',
    whiteSpace: 'normal',
    wordWrap: 'break-word',
    position: 'relative',
  }

  const arrowStyle: React.CSSProperties = {
    position: 'absolute',
    right: '-0.425rem',
    top: '50%',
    transform: 'translateY(-50%)',
    width: 0,
    height: 0,
    borderTop: '0.425rem solid transparent',
    borderBottom: '0.425rem solid transparent',
    borderLeft: '0.425rem solid var(--base-bg)',
  }

  return createPortal(
    <div style={tooltipStyle}>
      <div style={contentStyle}>
        {content}
        <div style={arrowStyle} />
      </div>
    </div>,
    document.body,
  )
}

export const LibraryTableAttachButton = ({
  isAttached,
  onClick,
  disabled,
  disabledReason,
  allowDetach = true,
}: {
  isAttached: boolean
  onClick: (event?: React.MouseEvent) => void
  disabled?: boolean
  disabledReason?: string
  allowDetach?: boolean
}) => {
  const [hovered, setHovered] = useState(false)
  const [showTooltip, setShowTooltip] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)

  const baseClassName =
    'LibraryTableAttachButton w-auto font-bold transition-all duration-500 ease-in-out'

  return (
    <>
      <Button
        kind="default"
        onClick={(e) => {
          e?.stopPropagation()
          onClick()
        }}
        className={classNames(baseClassName, {
          [`${baseClassName}--selected !bg-success text-tint-light !border-success`]: isAttached,
          'hover:!border-warning-400 hover:!bg-warning-400':
            isAttached && allowDetach,
        })}
        onMouseOver={() => {
          setHovered(true)
          if (disabled && disabledReason) {
            setShowTooltip(true)
          }
        }}
        onMouseOut={() => {
          setHovered(false)
          setShowTooltip(false)
        }}
        disabled={disabled}
        buttonRef={buttonRef}
      >
        {(() => {
          if (hovered && isAttached && allowDetach) return 'REMOVE'
          if (isAttached) return 'SELECTED'
          return 'SELECT'
        })()}
      </Button>
      {disabled && disabledReason && (
        <CustomTooltip
          content={disabledReason}
          targetRef={buttonRef}
          show={showTooltip}
        />
      )}
    </>
  )
}
